import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  PlaylistPlay,
  PauseCircle,
  People,
  Refresh,
  FilterList,
  CheckCircle,
  Error,
  Schedule,
  Group
} from '@mui/icons-material';
import { leadFlowService, LEAD_FLOW_ACTIONS } from '../../services/leadFlow';
import { format } from 'date-fns';

const LeadFlowControl = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [reason, setReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({ total_users: 0, active_flow: 0, paused_flow: 0 });

  useEffect(() => {
    fetchLeadFlowData();
  }, []);

  const fetchLeadFlowData = async () => {
    try {
      setLoading(true);
      const data = await leadFlowService.getLeadFlowDashboard();
      setUsers(data.users || []);
      setStats({
        total_users: data.total_users || 0,
        active_flow: data.active_flow || 0,
        paused_flow: data.paused_flow || 0,
      });
    } catch (error) {
      showSnackbar('Failed to fetch lead flow data', 'error');
      console.error('Error fetching lead flow data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSingleAction = async (user, action) => {
    setCurrentUser(user);
    setCurrentAction(action);
    
    if (action === LEAD_FLOW_ACTIONS.RESUME) {
      // Resume immediately without dialog
      await executeSingleAction(user, action, '');
    } else {
      // Show dialog for pause action
      setDialogOpen(true);
    }
  };

  const executeSingleAction = async (user, action, actionReason) => {
    try {
      setLoading(true);
      let result;
      
      if (action === LEAD_FLOW_ACTIONS.PAUSE) {
        result = await leadFlowService.pauseLeadFlow(user.id, actionReason);
      } else {
        result = await leadFlowService.resumeLeadFlow(user.id);
      }
      
      showSnackbar(result.message || `Lead flow ${action}d for ${user.full_name}`, 'success');
      await fetchLeadFlowData(); // Refresh data
      
    } catch (error) {
      showSnackbar(`Failed to ${action} lead flow`, 'error');
      console.error(`Error ${action}ing lead flow:`, error);
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setReason('');
      setCurrentUser(null);
    }
  };

  const handleBulkAction = (action) => {
    if (selectedUsers.length === 0) {
      showSnackbar('Please select users first', 'warning');
      return;
    }
    
    setCurrentAction(action);
    
    if (action === LEAD_FLOW_ACTIONS.RESUME) {
      // Bulk resume immediately
      executeBulkAction(action, '');
    } else {
      // Show dialog for bulk pause
      setBulkDialogOpen(true);
    }
  };

  const executeBulkAction = async (action, actionReason) => {
    try {
      setLoading(true);
      const result = await leadFlowService.bulkControlLeadFlow(selectedUsers, action, actionReason);
      
      const successCount = result.success_count || 0;
      const totalCount = selectedUsers.length;
      
      showSnackbar(
        `${action === LEAD_FLOW_ACTIONS.PAUSE ? 'Paused' : 'Resumed'} lead flow for ${successCount}/${totalCount} users`,
        successCount === totalCount ? 'success' : 'warning'
      );
      
      await fetchLeadFlowData(); // Refresh data
      setSelectedUsers([]);
      
    } catch (error) {
      showSnackbar(`Failed to ${action} lead flow for selected users`, 'error');
      console.error(`Error bulk ${action}ing lead flow:`, error);
    } finally {
      setLoading(false);
      setBulkDialogOpen(false);
      setReason('');
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusChip = (user) => {
    const isActive = user.lead_flow_status?.enabled;
    
    return (
      <Chip
        icon={isActive ? <CheckCircle /> : <PauseCircle />}
        label={isActive ? 'Active' : 'Paused'}
        color={isActive ? 'success' : 'warning'}
        size="small"
      />
    );
  };

  const getPauseInfo = (user) => {
    if (user.lead_flow_status?.enabled) return null;
    
    const pausedBy = user.lead_flow_status?.paused_by;
    const pausedAt = user.lead_flow_status?.paused_at;
    const reason = user.lead_flow_status?.pause_reason;
    
    return (
      <Box sx={{ mt: 0.5 }}>
        {pausedBy && (
          <Typography variant="caption" color="text.secondary" display="block">
            Paused by: {pausedBy}
          </Typography>
        )}
        {pausedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Since: {format(new Date(pausedAt), 'MMM dd, yyyy HH:mm')}
          </Typography>
        )}
        {reason && (
          <Typography variant="caption" color="warning.main" display="block">
            Reason: {reason}
          </Typography>
        )}
      </Box>
    );
  };

  const filteredUsers = users.filter(user => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return user.lead_flow_status?.enabled;
    if (filterStatus === 'paused') return !user.lead_flow_status?.enabled;
    return true;
  });

  return (
    <Box>
      {/* Header with Stats */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaylistPlay color="primary" />
          Lead Flow Control
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">{stats.total_users}</Typography>
                <Typography variant="body2" color="text.secondary">Total Users</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">{stats.active_flow}</Typography>
                <Typography variant="body2" color="text.secondary">Active Flow</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main">{stats.paused_flow}</Typography>
                <Typography variant="body2" color="text.secondary">Paused Flow</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrow />}
              onClick={() => handleBulkAction(LEAD_FLOW_ACTIONS.RESUME)}
              disabled={selectedUsers.length === 0 || loading}
            >
              Resume Selected ({selectedUsers.length})
            </Button>
            
            <Button
              variant="contained"
              color="warning"
              startIcon={<Pause />}
              onClick={() => handleBulkAction(LEAD_FLOW_ACTIONS.PAUSE)}
              disabled={selectedUsers.length === 0 || loading}
            >
              Pause Selected ({selectedUsers.length})
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchLeadFlowData}
              disabled={loading}
            >
              Refresh
            </Button>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filter Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filter Status"
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="active">Active Only</MenuItem>
                <MenuItem value="paused">Paused Only</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          {loading && filteredUsers.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Team Lead</TableCell>
                    <TableCell>Territories</TableCell>
                    <TableCell>Specializations</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleUserSelect(user.id)}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.full_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{user.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={user.role.toUpperCase()} 
                          size="small" 
                          color="primary" 
                        />
                      </TableCell>
                      
                      <TableCell>
                        {user.team_lead ? (
                          <Typography variant="body2">{user.team_lead}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.assigned_cities?.map((city, index) => (
                            <Chip key={index} label={city} size="small" variant="outlined" color="info" />
                          ))}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.assigned_diseases?.map((disease, index) => (
                            <Chip key={index} label={disease} size="small" variant="outlined" color="success" />
                          ))}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box>
                          {getStatusChip(user)}
                          {getPauseInfo(user)}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {user.lead_flow_status?.enabled ? (
                            <Tooltip title="Pause Lead Flow">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleSingleAction(user, LEAD_FLOW_ACTIONS.PAUSE)}
                                disabled={loading}
                              >
                                <Pause />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Resume Lead Flow">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleSingleAction(user, LEAD_FLOW_ACTIONS.RESUME)}
                                disabled={loading}
                              >
                                <PlayArrow />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {filteredUsers.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Group sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">
                          No users found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Single Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentAction === LEAD_FLOW_ACTIONS.PAUSE ? 'Pause Lead Flow' : 'Resume Lead Flow'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {currentAction === LEAD_FLOW_ACTIONS.PAUSE 
              ? `Pausing lead flow for ${currentUser?.full_name}. Please provide a reason:`
              : `Resume lead flow for ${currentUser?.full_name}?`
            }
          </Typography>
          
          {currentAction === LEAD_FLOW_ACTIONS.PAUSE && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for pausing"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., User on vacation, Training period, etc."
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => executeSingleAction(currentUser, currentAction, reason)}
            variant="contained"
            color={currentAction === LEAD_FLOW_ACTIONS.PAUSE ? 'warning' : 'success'}
            disabled={loading || (currentAction === LEAD_FLOW_ACTIONS.PAUSE && !reason.trim())}
          >
            {loading ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bulk {currentAction === LEAD_FLOW_ACTIONS.PAUSE ? 'Pause' : 'Resume'} Lead Flow
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {currentAction === LEAD_FLOW_ACTIONS.PAUSE 
              ? `Pausing lead flow for ${selectedUsers.length} users. Please provide a reason:`
              : `Resume lead flow for ${selectedUsers.length} users?`
            }
          </Typography>
          
          {currentAction === LEAD_FLOW_ACTIONS.PAUSE && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for pausing"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., End of month processing, System maintenance, etc."
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => executeBulkAction(currentAction, reason)}
            variant="contained"
            color={currentAction === LEAD_FLOW_ACTIONS.PAUSE ? 'warning' : 'success'}
            disabled={loading || (currentAction === LEAD_FLOW_ACTIONS.PAUSE && !reason.trim())}
          >
            {loading ? <CircularProgress size={20} /> : `${currentAction === LEAD_FLOW_ACTIONS.PAUSE ? 'Pause' : 'Resume'} ${selectedUsers.length} Users`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadFlowControl;